/**
 * How many referral rewards are earned but cannot be credited (COL-368).
 *
 * The tenant dashboard's tab strip needs this for the Referrals pip, and it is
 * the only fact behind the strip that `Dashboard.tsx` does not already derive
 * for a card of its own — `ReferralsPanel` fetches it, and the panel only
 * mounts once the tenant is already looking at the Referrals tab.
 *
 * Reuses `ReferralsPanel`'s exact React Query key, so with that tab open
 * nothing is fetched twice. Every OTHER fact behind the strip stays fresh via
 * a realtime channel that lives at `Dashboard.tsx`'s own top level, mounted
 * for the whole dashboard session regardless of which tab is active — this
 * hook installs its own `referrals`-table subscription for the same reason,
 * rather than depending on `ReferralsPanel`'s identical one, which Radix
 * unmounts (and so unsubscribes) the moment the tenant leaves the Referrals
 * tab. Without this, a reward going stale (or un-stalling) while the tenant
 * sits on Payments/Contract/Check-in would sit unreflected in the pip until
 * something unrelated forced a refetch (a remount, a window refocus).
 *
 * Returns 0 — no pip — while the query is pending or has failed. A pip that
 * appears on a failed request would send the tenant to a panel that is itself
 * showing an error, and a stalled reward is not time-critical enough to be
 * worth guessing about.
 */
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { EMPTY_REFERRALS, type MyReferrals } from "@/lib/referrals";

export function useReferralRewardStalled(): number {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data } = useQuery<MyReferrals>({
    queryKey: ["my-referrals", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("my_referrals");
      if (error) throw new Error(error.message);
      // jsonb: the MyReferrals shape, or null.
      return (data as unknown as MyReferrals | null) ?? EMPTY_REFERRALS;
    },
    enabled: !!user?.id,
  });

  // Dashboard-lifetime sibling of ReferralsPanel's own `referrals` subscription
  // (distinct channel name — this one has to keep running while that panel is
  // unmounted, so it cannot share a channel instance with it).
  useEffect(() => {
    if (!user?.id) return undefined;
    const channel = supabase
      .channel(`dashboard-referral-reward-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "referrals",
          filter: `referrer_user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["my-referrals", user.id],
          });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  const stalled = Number(data?.reward_stalled_count ?? 0);
  return Number.isFinite(stalled) && stalled > 0 ? stalled : 0;
}
